/**
 * Réduction des images AVANT envoi — optimisation de bande passante, pas un
 * contrôle de sécurité.
 *
 * Le contrôle est côté serveur (`apps/api/src/core/images.ts`) : il décode,
 * borne, dépouille et ré-encode tout ce qu'il reçoit, quoi qu'envoie le client.
 * Ce module ne sert qu'à ne pas faire monter 8 Mo depuis un chantier en 4G
 * faible pour que le serveur en garde 300 Ko. En cas de doute, il RENVOIE LE
 * FICHIER D'ORIGINE : une réduction ratée ne doit jamais empêcher un
 * technicien de déposer sa preuve.
 */

interface ShrinkTarget {
  /** Côté le plus long visé. Au-delà, l'image est réduite. */
  readonly maxSide: number;
  readonly quality: number;
  /** En dessous, l'envoi direct coûte moins cher qu'un ré-encodage. */
  readonly skipUnderBytes: number;
}

/**
 * Seuls les dépôts photographiques sont réduits.
 * - `logo` : petit, et sa transparence comme sa netteté sont contractuelles ;
 * - `signature` : preuve produite par notre canvas, quelques kilo-octets ;
 * - `scan_ocr` : la pièce d'une dépense garde sa résolution, c'est ce qu'on envoie en comptabilité.
 * Le serveur borne ces trois-là de son côté ; on ne les dégrade pas ici.
 */
const TARGETS: Record<string, ShrinkTarget> = {
  // Le serveur recadre ensuite en 512 px : on lui laisse une marge de qualité.
  avatar: { maxSide: 1024, quality: 0.85, skipUnderBytes: 200 * 1024 },
  // Même côté que le serveur : rien à gagner à monter plus grand.
  photo: { maxSide: 2560, quality: 0.82, skipUnderBytes: 400 * 1024 },
  // Photo de preuve d'un objectif : une photo comme les autres, montrée à moins de monde.
  preuve_objectif: { maxSide: 2560, quality: 0.82, skipUnderBytes: 400 * 1024 },
};

function canShrink(): boolean {
  return typeof createImageBitmap === 'function'
    && typeof document !== 'undefined'
    && typeof HTMLCanvasElement !== 'undefined';
}

/**
 * Renvoie une version réduite, ou le fichier d'origine si la réduction n'est
 * ni possible, ni souhaitable, ni profitable.
 */
export async function shrinkForUpload(
  file: File | Blob,
  kind: string,
): Promise<File | Blob> {
  const target = TARGETS[kind];
  if (!target || !canShrink()) return file;
  if (!file.type.startsWith('image/')) return file;
  if (file.size <= target.skipUnderBytes) return file;

  let bitmap: ImageBitmap | undefined;
  try {
    // `from-image` applique l'orientation EXIF : sans cela, une photo prise à
    // la verticale partirait couchée, puisque le ré-encodage perd l'EXIF.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const ratio = Math.min(1, target.maxSide / Math.max(bitmap.width, bitmap.height));
    if (ratio >= 1) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * ratio);
    canvas.height = Math.round(bitmap.height * ratio);
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/webp', target.quality);
    });
    // Un ré-encodage qui n'allège pas n'a aucune raison d'être envoyé.
    if (!blob || blob.size >= file.size) return file;
    return blob;
  } catch {
    return file;
  } finally {
    bitmap?.close();
  }
}

/** Le nom envoyé doit désigner ce que le fichier EST devenu. */
export function uploadFilename(
  original: string, sent: File | Blob, source: File | Blob,
): string {
  if (sent === source) return original;
  return `${original.replace(/\.[^./\\]{1,12}$/, '') || 'image'}.webp`;
}
